import {useEffect, useState} from "react";
import {fetchWrapper} from "../../_services/fetch";
import {useAuth} from "../../context";
import {endpoints, handleError, handleSuccess} from "../../utils2";
import {centsToUnits} from "../backend2/admin/utilities/formatCurrency";


const defaultValues = {
    bsk_token: null,
    address_id: "",
    billing_id: "",
    carrier_id: "",
    payment_id: "",
    coupon: "",
    bsk_price: 0,
    coupon_price: 0,
    total_price: 0,
    articles: [],
    created_at: null,
    updated_at: null,
    order_number: ""
}

const centsToPrices = articles => articles.map(o => ({
    ...o,
    unit_price: centsToUnits(o.unit_price),
    base_price: centsToUnits(o.base_price),
    total_price: centsToUnits(o.total_price)
}))

export function useBasket(getBasket = true) {
    const {token, bskToken, setBskToken} = useAuth()
    const {value, setValue, loadingValue, setLoadingValue, errorValue, setErrorValue} = useBasketList(null, getBasket)

    const handleErrorInt = err => {
        setErrorValue(err)
        return Promise.reject(err)
    }

    function addArticle(prodId, quantity) {
        setLoadingValue(true);
        const payload = {product_id: prodId, quantity: quantity, bsk_token: bskToken, auth_token: token?.jwt || null};

        return fetchWrapper.post(endpoints.cart.add(), payload)
            .then(handleSuccess)
            .then(setBskToken)
            .catch(handleError)
            .catch(handleErrorInt)
            .finally(() => setLoadingValue(false))
    }

    function editArticle(values) {
        if (JSON.stringify(values) === JSON.stringify(value)) {
            // Skip POST if data was not changed, e.g., user went back and forth
            return Promise.resolve()
        }

        setLoadingValue(true);
        const payload = {...values, auth_token: token?.jwt || null};

        return fetchWrapper.post(endpoints.cart.edit(), payload)
            .then(handleSuccess)
            .then(data => {
                setValue({
                    ...data,
                    articles: centsToPrices(data.articles),
                    bsk_price: centsToUnits(data.bsk_price),
                    coupon_price: centsToUnits(data.coupon_price),
                    total_price: centsToUnits(data.total_price)
                })
                setBskToken(data.bsk_token)
            })
            .catch(handleError)
            .catch(handleErrorInt)
            .finally(() => setLoadingValue(false))

    }

    function submitOrder(paypal_transaction_id = null) {
        setLoadingValue(true);
        const payload = {bsk_token: bskToken, paypal_transaction_id: paypal_transaction_id};

        return fetchWrapper.post(endpoints.cart.submit(), payload, token.jwt)
            .then(handleSuccess)
            .then(resp => {
                setBskToken(null)
                return resp
            })
            .catch(handleError)
            .catch(handleErrorInt)
            .finally(() => setLoadingValue(false))
    }

    return {
        basket: value,
        addArticle,
        editArticle,
        submitOrder,
        errorArticle: errorValue,
        loadingArticle: loadingValue,
        clearErrorArticle: () => {
            // Safety delete basket's token in case of errors
            setBskToken(null)
            setErrorValue(null)
        }
    }
}

export function useBasketList(defaultValues, getBasket = true) {
    const {bskToken, setBskToken} = useAuth()

    const [loadingValue, setLoadingValue] = useState(true)
    const [errorValue, setErrorValue] = useState(null)
    const [value, setValue] = useState(defaultValues)

    useEffect(() => {
        const controller = new AbortController()

        if (bskToken && getBasket) {
            setErrorValue(null)
            setLoadingValue(true)
            const payload = {bsk_token: bskToken}

            // Fetch options
            const requestOptions = {
                method: "POST",
                signal: controller.signal,
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(payload)
            }

            fetchWrapper.generic(endpoints.cart.get(), requestOptions)
                .then(handleSuccess)
                .then(data => {
                    setValue({
                        ...data,
                        articles: centsToPrices(data.articles),
                        bsk_price: centsToUnits(data.bsk_price),
                        coupon_price: centsToUnits(data.coupon_price),
                        total_price: centsToUnits(data.total_price)
                    })
                    setLoadingValue(false)
                })
                .catch(err => {
                    if (err?.status === 410) {
                        // 410 means basket token expired
                        setBskToken(null)
                        return Promise.resolve(defaultValues)
                    }
                    return Promise.reject(err)
                })
                .catch(handleError)
                .catch(err => {
                    setErrorValue(err)
                    setLoadingValue(false)
                })
            // Do not use finally, because triggered also by "AbortError"
            // .finally(() =>  setLoadingValue(false))
        } else {
            setValue(defaultValues)
            setLoadingValue(false)
        }

        return () => {
            controller.abort()
            setLoadingValue(false)
        }
    }, [bskToken]);


    return {
        value,
        setValue,
        loadingValue,
        setLoadingValue,
        errorValue,
        setErrorValue,
        clearError: () => setErrorValue(null)
    }
}

export function useBasketCount() {
    const {bskToken, setBskToken} = useAuth()

    const [loadingValue, setLoadingValue] = useState(false)
    const [errorValue, setErrorValue] = useState(null)
    const [value, setValue] = useState(null)

    useEffect(() => {
        const controller = new AbortController()

        if (bskToken) {
            setErrorValue(null)
            setLoadingValue(true)
            const payload = {bsk_token: bskToken}

            // Fetch options
            const requestOptions = {
                method: "POST",
                signal: controller.signal,
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(payload)
            }

            fetchWrapper.generic(endpoints.cart.count(), requestOptions)
                .then(handleSuccess)
                .then(setValue)
                .catch(err => {
                    if (err?.status === 410) {
                        // 410 means basket token expired
                        setBskToken(null)
                        return Promise.resolve(null)
                    }
                    return Promise.reject(err)
                })
                .catch(handleError)
                .catch(setErrorValue)
                .finally(() => setLoadingValue(false))
        } else {
            setValue(null)
        }

        return () => {
            controller.abort()
            setLoadingValue(false)
        }
    }, [bskToken]);


    return {
        count: value,
        setValue,
        loadingValue,
        setLoadingValue,
        errorValue,
        setErrorValue,
        clearError: () => setErrorValue(null)
    }
}